import { createContext, type Dispatch, type ReactNode, type SetStateAction, useContext, useState } from 'react';

// Panel widths (px) by size variant; shared so other canvas chrome (e.g. the undo control) can size to it.
export const PANEL_SIZE_WIDTHS = { md: 500, lg: 880, xl: 1240 } as const;

// The active node registers a descriptor of primitives; its body/footer JSX is portaled into the panel's
// DOM slots, not stored here (storing live JSX in state would loop).
export type NodePanelDescriptor = {
  nodeId: string;
  form?: string;
  canEdit: boolean;
  hasCustomFooter?: boolean;
  size?: 'md' | 'lg' | 'xl';
  isDirty: boolean;
  // A sub-editor in the body is open; it saves or cancels before the panel can.
  isSaveBlocked?: boolean;
  // A node added this session and never saved. Its Save stays enabled while clean so the form's own
  // validation, not a disabled button, tells the user what is missing.
  isNew?: boolean;
  clearDirty: () => void;
  onClose: () => void;
  // Panel isn't anchored to a graph node (e.g. Test playbook). NodePanel pans the whole canvas
  // clear of the panel instead of positioning a specific node beside it.
  anchorless?: boolean;
};

/** Width in px of the currently-open panel, or 0 when none is open. */
export const panelWidthOf = (descriptor: NodePanelDescriptor | null): number =>
  descriptor ? PANEL_SIZE_WIDTHS[descriptor.size ?? 'md'] : 0;

type NodePanelContextValue = {
  descriptor: NodePanelDescriptor | null;
  setDescriptor: Dispatch<SetStateAction<NodePanelDescriptor | null>>;
  bodySlot: HTMLElement | null;
  setBodySlot: (el: HTMLElement | null) => void;
  footerSlot: HTMLElement | null;
  setFooterSlot: (el: HTMLElement | null) => void;
};

const NodePanelContext = createContext<NodePanelContextValue | null>(null);

export const NodePanelProvider = ({ children }: { children: ReactNode }) => {
  const [descriptor, setDescriptor] = useState<NodePanelDescriptor | null>(null);
  const [bodySlot, setBodySlot] = useState<HTMLElement | null>(null);
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  const value = { descriptor, setDescriptor, bodySlot, setBodySlot, footerSlot, setFooterSlot };
  return <NodePanelContext.Provider value={value}>{children}</NodePanelContext.Provider>;
};

// No-op fallback so a node rendered outside the builder (e.g. Storybook without the harness) doesn't crash.
const NOOP: NodePanelContextValue = {
  descriptor: null,
  setDescriptor: () => {},
  bodySlot: null,
  setBodySlot: () => {},
  footerSlot: null,
  setFooterSlot: () => {},
};

export const useNodePanel = () => useContext(NodePanelContext) ?? NOOP;
